'use client'

import React, { useState, useMemo } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { HugeiconsIcon } from '@hugeicons/react'
import { Alert01Icon, ArrowDown01Icon, Search01Icon, Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { cn } from '@/lib/utils'

type MultiSelectOption = string | {
  value: string
  label: string
  group?: string
  description?: string
  writeScoped?: boolean
  dotColor?: 'green' | 'red'
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onSelectionChange: (selected: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  className?: string
  disabled?: boolean
  recommended?: string[]
}

export function MultiSelect({
  options,
  selected,
  onSelectionChange,
  placeholder = 'Select',
  searchPlaceholder = 'Search',
  className,
  disabled = false,
  recommended = [],
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})

  const normalizedOptions = useMemo(() => options.map((option) => (
    typeof option === 'string' ? { value: option, label: option } : option
  )), [options])

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return normalizedOptions
    const query = searchQuery.toLowerCase()
    return normalizedOptions.filter(option => (
      option.label.toLowerCase().includes(query) ||
      option.value.toLowerCase().includes(query) ||
      option.group?.toLowerCase().includes(query)
    ))
  }, [normalizedOptions, searchQuery])

  const groupedOptions = useMemo(() => {
    return filteredOptions.reduce<Record<string, typeof filteredOptions>>((groups, option) => {
      const group = option.group || 'Options'
      groups[group] = groups[group] || []
      groups[group].push(option)
      return groups
    }, {})
  }, [filteredOptions])

  const selectedOptions = useMemo(() => {
    return selected.map((value) => normalizedOptions.find((option) => option.value === value) ?? { value, label: value })
  }, [normalizedOptions, selected])

  const handleToggle = (option: string) => {
    if (selected.includes(option)) {
      onSelectionChange(selected.filter(s => s !== option))
    } else {
      onSelectionChange([...selected, option])
    }
  }

  const handleSelectAll = () => {
    if (selected.length === filteredOptions.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(filteredOptions.map((option) => option.value))
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onSelectionChange([])
  }

  const displayText = selected.length === 0
    ? placeholder
    : selected.length === 1
    ? normalizedOptions.find((option) => option.value === selected[0])?.label ?? selected[0]
    : `${selected.length} selected`

  const dotClass = (dotColor?: 'green' | 'red') => cn(
    'size-2 shrink-0 rounded-full',
    dotColor === 'red' ? 'bg-red-500' : 'bg-green-500',
  )

  return (
    <TooltipProvider>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full h-12 justify-between bg-atrium-inner text-atrium-black text-xs border-atrium-border rounded-xl px-4 hover:bg-atrium-content',
              open && 'border-atrium-border-dark',
              className,
            )}
          >
            <span className={cn('truncate', selected.length === 0 && 'text-atrium-muted')}>
              {displayText}
            </span>
            <div className="flex items-center gap-2">
              {selected.length > 0 && (
                // Use a span to avoid nesting a <button> inside the trigger <Button>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleClear(e as any)
                  }}
                  className="flex items-center justify-center outline-none"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <HugeiconsIcon icon={Cancel01Icon} className="size-4 text-atrium-muted hover:text-atrium-black" />
                </span>
              )}
              <HugeiconsIcon
                icon={ArrowDown01Icon}
                className={cn(
                  'size-4 text-atrium-muted transition-transform',
                  open && 'rotate-180',
                )}
              />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="rounded-xl border border-atrium-border bg-atrium-background p-0 text-atrium-black"
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          <div className="space-y-3 p-2">
            {selectedOptions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-b border-atrium-border pb-2">
                {selectedOptions.map((option) => (
                  <span
                    key={option.value}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-atrium-border bg-atrium-inner py-1 pl-2 pr-1 text-xs text-atrium-black"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={dotClass(option.dotColor)} />
                      </TooltipTrigger>
                      <TooltipContent>
                        {option.dotColor === 'red' ? 'Can mutate live data' : 'Read-only'}
                      </TooltipContent>
                    </Tooltip>
                    <span className="truncate">{option.label}</span>
                    {option.writeScoped && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex text-amber-600 dark:text-amber-400">
                            <HugeiconsIcon icon={Alert01Icon} className="size-3.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>This tool can mutate live data.</TooltipContent>
                      </Tooltip>
                    )}
                    <button
                      type="button"
                      aria-label={`Remove ${option.label}`}
                      className="ml-0.5 inline-flex size-4 items-center justify-center rounded-full text-atrium-muted transition-colors hover:bg-atrium-content hover:text-atrium-black"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleToggle(option.value)
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <HugeiconsIcon icon={Cancel01Icon} className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search bar */}
            <div className="relative">
              <HugeiconsIcon icon={Search01Icon} className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-atrium-muted" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 border-atrium-border bg-atrium-inner pl-9 text-xs text-atrium-black placeholder:text-atrium-muted"
              />
            </div>

            {/* Select All */}
            <div className="flex items-center justify-between border-b border-atrium-border pb-2">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-sm font-medium text-accent-600 hover:text-accent-700"
              >
                Select All
              </button>
              <span className="text-xs text-atrium-muted">
                {selected.length} of {filteredOptions.length} selected
              </span>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 pb-1 text-[11px] text-atrium-muted">
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2 shrink-0 rounded-full bg-green-500" />
                Read-only
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2 shrink-0 rounded-full bg-red-500" />
                Can mutate live data
              </span>
            </div>

            {/* Options list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="py-4 text-center text-sm text-atrium-muted">No options found</div>
              ) : (
                Object.entries(groupedOptions).map(([group, groupOptions]) => {
                  const collapsed = collapsedGroups[group] === true

                  return (
                    <div key={group} className="py-1">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs font-semibold uppercase tracking-wide text-atrium-muted hover:bg-atrium-content"
                        onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group]: !collapsed }))}
                      >
                        <span>{group}</span>
                        <HugeiconsIcon
                          icon={ArrowDown01Icon}
                          className={cn('size-3.5 transition-transform', collapsed && '-rotate-90')}
                        />
                      </button>
                      {!collapsed && groupOptions.map((option) => (
                        <div
                          key={option.value}
                          className="flex cursor-pointer items-start space-x-2 rounded p-2 hover:bg-atrium-content"
                          onClick={() => handleToggle(option.value)}
                        >
                          <Checkbox
                            checked={selected.includes(option.value)}
                            onCheckedChange={() => handleToggle(option.value)}
                            className="mt-1 border-atrium-border data-[state=checked]:border-accent-600 data-[state=checked]:bg-accent-600 data-[state=checked]:!text-white data-[state=unchecked]:bg-atrium-background [&_[data-state=checked]>svg]:!text-white"
                          />
                          <label className="min-w-0 flex-1 cursor-pointer text-sm text-atrium-black">
                            <span className="flex min-w-0 items-center gap-2">
                              {option.dotColor && <span className={dotClass(option.dotColor)} />}
                              <span className="truncate">{option.label}</span>
                              {recommended.includes(option.value) && (
                                <span className="shrink-0 rounded-full border border-accent-500/30 bg-accent-500/10 px-2 py-0.5 text-xs font-medium text-accent-600">
                                  Recommended
                                </span>
                              )}
                            </span>
                            {option.description && (
                              <span className="mt-0.5 block text-xs leading-4 text-atrium-muted">
                                {option.description}
                              </span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  )
}
